import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { MessageModal } from 'common/components';
import { ApiStatus, ButtonLabel, MessageType } from 'common/enums';
import {
    DELETE_DISABLED,
    DELETE_POPUP_SUMMARY_PART1,
    DELETE_POPUP_SUMMARY_PART2,
    DELETE_POPUP_TITLE,
    FAILURE_HEADER
} from 'common/config';
import { EntityVM } from 'common/models/data/entity.model';
import { RootState } from 'common/redux/core/root.reducer';
import { appStateActions } from 'common/redux/app-state';

import { masterDataActions } from 'modules/master-data/redux';
import { getDeleteFeasibility } from 'modules/master-data/redux/masterData.selector';

type EntityDeleteProps = {
    data: EntityVM;
    onClose: () => void;
};

export default function EntityDelete({ data, onClose }: EntityDeleteProps) {
    const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
    const deleteFeasibility = useSelector((state: RootState) => getDeleteFeasibility(state));
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(
            masterDataActions.getDeleteFeasibility({
                id: data.id,
                entityType: data.entityType
            })
        );
    }, [data.entityType, data.id, dispatch]);

    useEffect(() => {
        if (deleteFeasibility.status === ApiStatus.SUCCESS) {
            if (deleteFeasibility.data) {
                setIsConfirmationVisible(true);
            } else {
                onClose();
                dispatch(
                    appStateActions.showMessageModal({
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: DELETE_DISABLED,
                        type: MessageType.Error
                    })
                );
            }
            dispatch(masterDataActions.resetDeleteFeasibility());
        } else if (deleteFeasibility.status === ApiStatus.ERROR) {
            dispatch(masterDataActions.resetDeleteFeasibility());
            onClose();
        }
    }, [deleteFeasibility, dispatch, onClose]);

    const handleConfirmationClose = () => {
        setIsConfirmationVisible(false);
        onClose();
    };

    const handleConfirmation = () => {
        dispatch(masterDataActions.deleteData(data));
        handleConfirmationClose();
    };

    return (
        <>
            {isConfirmationVisible && (
                <MessageModal
                    headerName={DELETE_POPUP_TITLE}
                    message={`${DELETE_POPUP_SUMMARY_PART1} this record. ${DELETE_POPUP_SUMMARY_PART2}`}
                    isVisible={isConfirmationVisible}
                    isSecondaryButtonVisible={true}
                    primaryButton={ButtonLabel.Ok}
                    secondaryButton={ButtonLabel.Cancel}
                    onPrimaryAction={handleConfirmation}
                    onSecondaryAction={handleConfirmationClose}
                />
            )}
        </>
    );
}
